import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import receptionService from '../../../services/receptionService';
import ReceptionModalShell, { ReceptionFormField, ReceptionModalSection } from '../ui/ReceptionModalShell';
import { BrowseTypePill } from '../browse/BrowseContactLink';
import { memberDisplayName } from '../browse/browseFormatters';

const TYPE_TONE = {
  AGENT: 'agent',
  CONJOINT: 'partner',
  CONJOINTE: 'partner',
  ENFANT: 'child'
};

function toDateInput(value) {
  if (!value) return '';
  const s = String(value);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

function buildForm(member) {
  return {
    nom: member?.nom || '',
    prenom: member?.prenom || '',
    sexe: member?.sexe || '',
    fonction: member?.fonction || '',
    date_naissance: toDateInput(member?.date_naissance)
  };
}

export default function ReceptionMemberEditModal({
  show,
  member,
  memberTypeLabels = {},
  onHide,
  onSaved
}) {
  const [form, setForm] = useState(buildForm(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setForm(buildForm(member));
    setError('');
  }, [show, member]);

  const typeLabel = memberTypeLabels[member?.type] || member?.type || '—';
  const typeTone = TYPE_TONE[member?.type] || 'neutral';

  const agentLabel = useMemo(() => {
    if (!member || member.type === 'AGENT') return null;
    const name = [member.agent_prenom, member.agent_nom].filter(Boolean).join(' ').trim();
    if (!name && !member.agent_matricule) return null;
    return [name, member.agent_matricule ? `mat. ${member.agent_matricule}` : null].filter(Boolean).join(' · ');
  }, [member]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.nom.trim() || form.nom.trim().length < 2) {
      return 'Le nom doit contenir au moins 2 caractères.';
    }
    if (form.prenom.trim().length > 100) {
      return 'Le prénom ne peut pas dépasser 100 caractères.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!member?.id || saving) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        nom: form.nom.trim(),
        prenom: form.prenom.trim() || null,
        sexe: form.sexe || null,
        fonction: form.fonction.trim() || null,
        date_naissance: form.date_naissance || null
      };
      const res = await receptionService.updateMemberProfile(member.id, payload);
      if (!res.success) {
        throw new Error(res.message || 'Mise à jour impossible');
      }
      onSaved?.(res.data || { ...member, ...payload });
      onHide?.();
    } catch (err) {
      setError(err.message || 'Mise à jour impossible');
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (saving) return;
    onHide?.();
  };

  return (
    <ReceptionModalShell
      show={show}
      onHide={close}
      size="lg"
      title={member ? `Modifier ${memberDisplayName(member)}` : 'Modifier adhérent'}
      subtitle={member?.matricule ? `Matricule ${member.matricule}` : 'Correction des informations'}
      disableClose={saving}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={saving}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" form="recv-member-edit-form" disabled={saving || !member}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enregistrement…
              </>
            ) : (
              'Enregistrer'
            )}
          </Button>
        </>
      }
    >
      {!member ? null : (
        <Form id="recv-member-edit-form" className="recv-form-modern" onSubmit={handleSubmit}>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          <ReceptionModalSection
            title="Rattachement"
            description="Ces éléments ne sont pas modifiables à l'accueil. Contactez l'administration pour un changement de société, matricule ou type."
          >
            <div className="recv-rdv-detail-badges mb-3">
              <BrowseTypePill tone={typeTone}>{typeLabel}</BrowseTypePill>
              {member.company_name ? (
                <span className="recv-member-detail-chip">{member.company_name}</span>
              ) : null}
            </div>
            <Row className="g-2 recv-member-edit-readonly">
              <Col md={6} xs={12}>
                <ReceptionFormField label="Matricule">
                  <Form.Control value={member.matricule || '—'} readOnly plaintext className="font-monospace" />
                </ReceptionFormField>
              </Col>
              <Col md={6} xs={12}>
                <ReceptionFormField label="Société">
                  <Form.Control value={member.company_name || '—'} readOnly plaintext />
                </ReceptionFormField>
              </Col>
              {agentLabel ? (
                <Col xs={12}>
                  <ReceptionFormField label="Agent rattaché">
                    <Form.Control value={agentLabel} readOnly plaintext />
                  </ReceptionFormField>
                </Col>
              ) : null}
            </Row>
          </ReceptionModalSection>

          <ReceptionModalSection title="Identité">
            <Row className="g-2">
              <Col md={6} xs={12}>
                <ReceptionFormField label="Nom" htmlFor="recv-member-nom" required>
                  <Form.Control
                    id="recv-member-nom"
                    value={form.nom}
                    onChange={(ev) => updateField('nom', ev.target.value)}
                    required
                    autoFocus
                  />
                </ReceptionFormField>
              </Col>
              <Col md={6} xs={12}>
                <ReceptionFormField label="Prénom" htmlFor="recv-member-prenom">
                  <Form.Control
                    id="recv-member-prenom"
                    value={form.prenom}
                    onChange={(ev) => updateField('prenom', ev.target.value)}
                    placeholder="Laisser vide si inconnu"
                  />
                </ReceptionFormField>
              </Col>
              <Col md={4} xs={12}>
                <ReceptionFormField label="Sexe" htmlFor="recv-member-sexe">
                  <Form.Select
                    id="recv-member-sexe"
                    value={form.sexe}
                    onChange={(ev) => updateField('sexe', ev.target.value)}
                  >
                    <option value="">— Non renseigné —</option>
                    <option value="M">Masculin</option>
                    <option value="F">Féminin</option>
                  </Form.Select>
                </ReceptionFormField>
              </Col>
              <Col md={8} xs={12}>
                <ReceptionFormField label="Fonction" htmlFor="recv-member-fonction">
                  <Form.Control
                    id="recv-member-fonction"
                    value={form.fonction}
                    onChange={(ev) => updateField('fonction', ev.target.value)}
                    placeholder="Poste / fonction"
                  />
                </ReceptionFormField>
              </Col>
              <Col md={6} xs={12}>
                <ReceptionFormField label="Date de naissance" htmlFor="recv-member-naissance">
                  <Form.Control
                    id="recv-member-naissance"
                    type="date"
                    value={form.date_naissance}
                    onChange={(ev) => updateField('date_naissance', ev.target.value)}
                  />
                </ReceptionFormField>
              </Col>
            </Row>
          </ReceptionModalSection>
        </Form>
      )}
    </ReceptionModalShell>
  );
}
